import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Menu, X, Settings} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import UserMenu from '@/components/UserMenu'

export default function ImmersiveLayout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const { isAdmin } = useAuth()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: 'Comprendre', href: '/comprendre' },
    { name: 'Expérimenter', href: '/experimenter' },
    { name: 'Transformation', href: '/transformation' },
    { name: 'Alignement', href: '/alignement' },
    { name: 'Accompagnements', href: '/accompagnements' },
    { name: 'Sanctuaire', href: '/sanctuaire' },
    { name: 'Contact', href: '/contact' }
  ]

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="min-h-screen bg-[#262626] text-[#f5f1ea]">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-40 w-96 h-96 rounded-full bg-[#c9a96e]/8 blur-3xl breathe" />
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 rounded-full bg-[#c9a96e]/5 blur-3xl breathe" style={{ animationDelay: '4s' }} />
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled ? 'bg-[#262626]/90 backdrop-blur-xl border-b border-[#c9a96e]/15' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="group flex items-center gap-3">
            <div className="w-9 h-9 border border-[#c9a96e]/40 rounded-full flex items-center justify-center transition-all duration-500 group-hover:border-[#c9a96e]">
              <div className="w-2 h-2 rounded-full bg-[#c9a96e] shimmer" />
            </div>
            <span className="font-serif text-lg tracking-wide text-[#f5f1ea]/90 group-hover:text-[#c9a96e] transition-colors duration-500">
              Laboratoire
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-xs tracking-widest uppercase transition-all duration-500 ${
                  isActive(item.href)
                    ? 'text-[#c9a96e]'
                    : 'text-[#f5f1ea]/60 hover:text-[#f5f1ea]'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="pl-6 ml-2 border-l border-[#c9a96e]/15">
              <UserMenu />
            </div>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-3">
            <UserMenu />
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-[#f5f1ea]/80 hover:text-[#c9a96e] transition-colors p-1"
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-[#262626]/98 backdrop-blur-xl border-t border-[#c9a96e]/15">
            <div className="px-6 py-8 flex flex-col gap-6">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-sm tracking-widest uppercase transition-colors ${
                    isActive(item.href) ? 'text-[#c9a96e]' : 'text-[#f5f1ea]/70'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`flex items-center gap-2 text-sm tracking-widest uppercase transition-colors ${
                    isActive('/admin') ? 'text-[#c9a96e]' : 'text-[#c9a96e]/70'
                  }`}
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Éditeur admin</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Page content */}
      <main className="relative z-10 pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#c9a96e]/10 mt-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="font-serif text-xl text-[#c9a96e] mb-4">Laboratoire de Transformation</h3>
              <p className="text-sm text-[#8a8580] leading-relaxed font-light">
                Un espace pour comprendre, ressentir, transformer.
              </p>
            </div>
            <div>
              <h4 className="text-xs tracking-widest uppercase text-[#f5f1ea]/80 mb-4">Explorer</h4>
              <ul className="space-y-3">
                {navigation.slice(1, 5).map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="text-sm text-[#8a8580] hover:text-[#c9a96e] transition-colors font-light"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs tracking-widest uppercase text-[#f5f1ea]/80 mb-4">Rejoindre</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/accompagnements" className="text-sm text-[#8a8580] hover:text-[#c9a96e] transition-colors font-light">
                    Accompagnements
                  </Link>
                </li>
                <li>
                  <Link to="/sanctuaire" className="text-sm text-[#8a8580] hover:text-[#c9a96e] transition-colors font-light">
                    Le Sanctuaire
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-[#8a8580] hover:text-[#c9a96e] transition-colors font-light">
                    Contact
                  </Link>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/@LaboratoireTransformationInterieur"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#8a8580] hover:text-[#c9a96e] transition-colors font-light"
                  >
                    Chaîne YouTube
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-[#c9a96e]/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[#8a8580] font-light tracking-wide">
              © 2024 Laboratoire de Transformation
            </p>
            <p className="text-xs text-[#c9a96e]/60 font-serif italic">
              Comprendre. Ressentir. Transformer.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
